import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../lib/api';

/** Подписка потребителя (1:N), источник — adapter-service через BFF (promt-73, Фаза 3). */
export interface Subscription {
  serviceSubscriptionId: string;
  partnerAccountId: string;
  partnerSubscriptionId: string;
  optionCode: string;
  serviceLabel: string | null;
  status: string;
  accessMode: string | null;
  servicePlan: string | null;
  endDate: string | null;
  maxMailboxes?: number | null;
  totalQuotaMb?: number | null;
}

export interface SubscriptionStats {
  mailboxCount: number;
  mailboxLimit: number;
  usedMb: number;
  limitMb: number;
  freeMb: number;
  forwardedCount: number;
}

export interface SubscriptionsResponse {
  items: Subscription[];
  activeSubscriptionId: string | null;
}

export const subscriptionKeys = {
  all: ['subscriptions'] as const,
};

export async function fetchSubscriptions(): Promise<SubscriptionsResponse> {
  const res = await apiFetch('/api/v1/subscriptions');
  if (res.status === 401) {
    return { items: [], activeSubscriptionId: null };
  }
  if (!res.ok) {
    throw new Error(`subscriptions request failed: ${String(res.status)}`);
  }
  // Ответ нормализуем: список подписок читают и layout, и экран выбора, поэтому неожиданная
  // форма тела не должна ронять приложение целиком.
  const body = (await res.json()) as Partial<SubscriptionsResponse> | null;
  return {
    items: Array.isArray(body?.items) ? body.items : [],
    activeSubscriptionId: body?.activeSubscriptionId ?? null,
  };
}

/** Список подписок + текущая активная (auto-select при единственной подписке — на стороне BFF). */
export function useSubscriptions(enabled = true) {
  return useQuery({
    queryKey: subscriptionKeys.all,
    queryFn: fetchSubscriptions,
    enabled,
    staleTime: 60_000,
  });
}

/** Выбор активной подписки (PUT /api/v1/subscriptions/active). */
export function useSetActiveSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (serviceSubscriptionId: string) => {
      const res = await apiFetch('/api/v1/subscriptions/active', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ serviceSubscriptionId }),
      });
      if (!res.ok) {
        throw new Error(`set active failed: ${String(res.status)}`);
      }
      return (await res.json()) as { activeSubscriptionId: string };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: subscriptionKeys.all });
      void qc.invalidateQueries({ queryKey: ['profile'] });
      // Данные почты/квоты зависят от выбранной подписки — обновляем.
      void qc.invalidateQueries({ queryKey: ['mailboxes'] });
      void qc.invalidateQueries({ queryKey: ['quota'] });
    },
  });
}

export const subscriptionStatKeys = {
  one: (id: string) => ['subscriptions', 'stats', id] as const,
};

export function useSubscriptionStats(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: subscriptionStatKeys.one(id ?? ''),
    queryFn: async (): Promise<SubscriptionStats> => {
      const res = await apiFetch(`/api/v1/subscriptions/${id ?? ''}/stats`);
      if (!res.ok) {
        throw new Error(`subscription stats failed: ${String(res.status)}`);
      }
      return (await res.json()) as SubscriptionStats;
    },
    enabled: enabled && Boolean(id),
    staleTime: 15_000,
  });
}

export function useRenameSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, serviceLabel }: { id: string; serviceLabel: string }) => {
      const res = await apiFetch(`/api/v1/subscriptions/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ serviceLabel }),
      });
      if (!res.ok) {
        throw new Error(`rename failed: ${String(res.status)}`);
      }
      return (await res.json()) as { serviceSubscriptionId: string; serviceLabel: string };
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: subscriptionKeys.all });
      void qc.invalidateQueries({ queryKey: subscriptionStatKeys.one(vars.id) });
    },
  });
}
